import { FormBlockerElementConfig, implementedElements } from './elements/FormBlockerElement/FormBlockerElement'
import { FormBlockerElementType } from './FormBlocker'

type ElementTypesForSpacing = keyof FormBlockerElementConfig | 'actions'

const defaultSpacing = 1

export const spacingOverrides = createSpacingOverrides()

// The result of this function is a number in rem units.
export function formBlockerElementSpacing(from: FormBlockerElementType, to: ElementTypesForSpacing): number {
  // First, look for spacing between these specific elements.
  const specificSpacing = spacingOverrides[from]?.[to]
  if (specificSpacing !== undefined) {
    return specificSpacing
  }

  // Else, return the default spacing.
  return defaultSpacing
}

function createSpacingOverrides() {
  const spacingOverrides: Partial<{
    [from in ElementTypesForSpacing]: Partial<{ [to in ElementTypesForSpacing]: number }>
  }> = {}

  function setSpacing({
    from,
    to,
    spacing,
  }: {
    from: ElementTypesForSpacing | 'all'
    to: ElementTypesForSpacing | 'all'
    spacing: number
  }) {
    const implementedElementNames = Object.keys(implementedElements) as (keyof FormBlockerElementConfig)[]

    if (from === 'all') {
      implementedElementNames.forEach(el => {
        setSpacing({ from: el, to, spacing })
      })
    } else if (to === 'all') {
      implementedElementNames.forEach(el => {
        setSpacing({ from, to: el, spacing })
        setSpacing({ from, to: 'actions', spacing })
      })
    } else {
      spacingOverrides[from] = spacingOverrides[from] || {}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      spacingOverrides[from]![to] = spacing

      // If the spacing ends up as the default, delete it so that testing is easier.
      if (spacing === defaultSpacing) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        delete spacingOverrides[from]![to]
      }
    }
  }

  // Note: Any spacing values can be overridden by spacing values defined later.

  // First, define spacing for elements that want non-default spacing above themselves.
  setSpacing({ from: 'all', to: 'actions', spacing: 0 })
  setSpacing({ from: 'all', to: 'copyable_element_group', spacing: 2 })
  setSpacing({ from: 'all', to: 'emoji_picker_element', spacing: 2.5 })
  setSpacing({ from: 'all', to: 'legal_text_element', spacing: 1.5 })
  setSpacing({ from: 'all', to: 'money_input_element', spacing: 2 })
  setSpacing({ from: 'all', to: 'selectable_row_element', spacing: 1.25 })
  setSpacing({ from: 'all', to: 'text_input_element', spacing: 2 })

  // Next, define spacing for elements that want non-default spacing below themselves.
  setSpacing({ from: 'avatar_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'call_to_action_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'card_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'customized_card_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'detail_row_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'local_image_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'merchant_transaction_picker_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'option_picker_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'remote_image_element', to: 'all', spacing: 1 })
  setSpacing({ from: 'selectable_row_element', to: 'all', spacing: 1.25 })
  setSpacing({ from: 'spacer_element', to: 'all', spacing: 0 })
  setSpacing({ from: 'text_element', to: 'all', spacing: 1 })

  // Lastly, define spacing between specific elements.
  setSpacing({ from: 'cell_default_element', to: 'cell_default_element', spacing: 2 })
  setSpacing({ from: 'emoji_picker_element', to: 'spacer_element', spacing: 0 })
  setSpacing({ from: 'spacer_element', to: 'button_element', spacing: 1 })
  setSpacing({ from: 'text_element', to: 'copyable_element_group', spacing: 2 })
  setSpacing({ from: 'text_element', to: 'emoji_picker_element', spacing: 2.5 })
  setSpacing({ from: 'text_element', to: 'money_input_element', spacing: 2 })
  setSpacing({ from: 'text_element', to: 'selectable_row_element', spacing: 1.25 })
  setSpacing({ from: 'text_element', to: 'spacer_element', spacing: 1 })
  setSpacing({ from: 'text_element', to: 'text_input_element', spacing: 2 })
  setSpacing({ from: 'text_element', to: 'address_element', spacing: 3 })
  setSpacing({ from: 'text_input_element', to: 'spacer_element', spacing: 0 })

  // Let fixed spacer elements to control any spacing by themselves.
  setSpacing({ from: 'fixed_spacer_element', to: 'all', spacing: 0 })
  setSpacing({ from: 'all', to: 'fixed_spacer_element', spacing: 0 })

  return spacingOverrides
}
